import { useSuperAdminShops } from "@/api/super-admin/use-super-admin-shops";
import type { ReactNode } from "react";
import { CustomerPortalTab } from "./customer-portal-tab";
import { SuperAdminsTab } from "./super-admins-tab";

type AdminShop = NonNullable<
  ReturnType<typeof useSuperAdminShops>["data"]
>["shops"][number];

export function AdminPageShell({
  title,
  description,
  actions,
  mainClassName,
  children,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  mainClassName?: string;
  children: ReactNode;
}) {
  // Opaque backgrounds only: the sidebar wrapper behind the inset is dark, any
  // translucent layer here turns the page grey and the muted text unreadable.
  return (
    <div className="flex min-h-full flex-col bg-slate-50">
      <header className="sticky top-0 z-10 border-b bg-background">
        <div className="mx-auto flex max-w-screen-2xl items-center justify-between gap-4 px-6 py-4">
          <div className="min-w-0">
            <h1 className="truncate text-xl font-semibold tracking-tight text-foreground">
              {title}
            </h1>
            {description && (
              <p className="mt-0.5 text-sm text-muted-foreground">
                {description}
              </p>
            )}
          </div>
          {actions}
        </div>
      </header>
      <main
        className={[
          "mx-auto w-full max-w-screen-2xl flex-1 space-y-6 px-6 py-6",
          mainClassName ?? "",
        ].join(" ")}
      >
        {children}
      </main>
    </div>
  );
}

/**
 * Pages that need the fleet list share the super admin overview query, so
 * moving between them hits the TanStack cache instead of refetching.
 */
function WithShops({
  title,
  children,
}: {
  title: string;
  children: (shops: AdminShop[]) => ReactNode;
}) {
  const { data, isPending, isError } = useSuperAdminShops();

  return (
    <AdminPageShell title={title}>
      {isPending ? (
        <p className="text-sm text-muted-foreground">Chargement des shops…</p>
      ) : isError || !data ? (
        <p className="text-sm text-destructive">
          Impossible de charger les shops.
        </p>
      ) : (
        children(data.shops)
      )}
    </AdminPageShell>
  );
}

export function AdminCustomerPortalPage() {
  return (
    <WithShops title="Portail client">
      {(shops) => <CustomerPortalTab shops={shops} />}
    </WithShops>
  );
}

export function AdminSuperAdminsPage() {
  return (
    <AdminPageShell title="Super admins">
      <SuperAdminsTab />
    </AdminPageShell>
  );
}
